import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { useAdmin } from './hooks/useAdmin';
import { CartProvider } from './contexts/CartContext';
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import Menu from './pages/Menu';
import Onboarding from './pages/owner/Onboarding';
import Dashboard from './pages/owner/Dashboard';
import MenuManagement from './pages/owner/MenuManagement';
import OrderQueue from './pages/owner/OrderQueue';
import ScanOrder from './pages/owner/ScanOrder';
import BusinessSettings from './pages/owner/BusinessSettings';
import MenuTheme from './pages/owner/MenuTheme';
import Sales from './pages/owner/Sales';
import RewardsConfig from './pages/owner/RewardsConfig';
import Profile from './pages/Profile';
import Account from './pages/Account';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import OrderSuccess from './pages/OrderSuccess';
import OrderStatus from './pages/OrderStatus';
import Admin from './pages/Admin';
import SignInRedirect from './pages/SignInRedirect';
import CustomerWelcome from './pages/CustomerWelcome';
import OwnerBottomNav from './components/OwnerBottomNav';
import OwnerGuard from './components/OwnerGuard';
import AuthCorner from './components/AuthCorner';
import OwnerStripeGate from './components/OwnerStripeGate';
import OwnerStoreClosedReminder from './components/OwnerStoreClosedReminder';
import OwnerOrderAlerts from './components/OwnerOrderAlerts';
import logo from './logo.svg';
import './App.css';

function Header() {
  const location = useLocation();
  const { isAdmin, loading: adminLoading } = useAdmin();

  const noHeaderPaths = ['/checkout/success', '/checkout/cancel', '/auth/callback', '/order/', '/customer', '/__test__/checkout/', '/__test__/auth/', '/__test__/order/', '/__test__/customer'];
  const isMenuPage = location.pathname.startsWith('/m/') || location.pathname.startsWith('/__test__/m/');
  const isLandingPage = location.pathname === '/' || location.pathname === '/__test__/';
  const isCustomerWelcomePage = location.pathname === '/customer' || location.pathname === '/__test__/customer';
  const isOwnerPath = location.pathname.startsWith('/owner/') || location.pathname.startsWith('/__test__/owner/');
  if (noHeaderPaths.some((p) => (p.endsWith('/') ? location.pathname.startsWith(p) : location.pathname === p))) return null;
  if (isMenuPage) return null;
  if (isLandingPage) return null;
  if (isCustomerWelcomePage) return null;

  return (
    <header className="fixed top-0 left-0 right-0 z-40 text-white" style={{ background: '#0f172a' }}>
      <div className="max-w-screen-md mx-auto px-4 pr-14 py-3 flex items-center justify-between relative">
        <Link to="/" className="flex items-center justify-center shrink-0 w-12 h-12 rounded-full bg-white header-logo-wrap" aria-label="Grab & Go home">
          <img src={logo} alt="Grab & Go" className="h-8 w-auto object-contain header-logo-spin" />
        </Link>
        <h1 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-lg font-semibold tracking-tight">
          Grab & Go
        </h1>
        <nav className="flex items-center gap-4">
          <SignedIn>
            {!isOwnerPath && !location.pathname.match(/^(\/__test__)?\/profile$/) && (
              <Link to="/profile" className="hover:text-slate-300">Dashboard</Link>
            )}
            {!adminLoading && isAdmin && (
              <Link to="/admin" className="hover:text-slate-300">Admin</Link>
            )}
          </SignedIn>
        </nav>
      </div>
    </header>
  );
}

function App() {
  const location = useLocation();
  const noHeaderPaths = ['/checkout/success', '/checkout/cancel', '/auth/callback', '/order/', '/__test__/checkout/', '/__test__/auth/', '/__test__/order/'];
  const isMenuPage = location.pathname.startsWith('/m/') || location.pathname.startsWith('/__test__/m/');
  const isLandingPage = location.pathname === '/' || location.pathname === '/__test__/';
  const isCustomerWelcomePage = location.pathname === '/customer' || location.pathname === '/__test__/customer';
  const isOwnerPage = (location.pathname.startsWith('/owner/') || location.pathname.startsWith('/__test__/owner/')) && !location.pathname.endsWith('/onboarding');
  const addTopPadding = !noHeaderPaths.some((p) => (p.endsWith('/') ? location.pathname.startsWith(p) : location.pathname === p)) && !isMenuPage && !isLandingPage && !isCustomerWelcomePage;
  const addBottomPadding = isOwnerPage;
  const isFullBleed = isMenuPage || isLandingPage || isCustomerWelcomePage;

  return (
    <CartProvider>
      <AuthCorner />
      <Header />
      {isOwnerPage && <OwnerBottomNav />}
      {isOwnerPage && (
        <SignedIn>
          <OwnerGuard>
            <OwnerStoreClosedReminder />
            <OwnerOrderAlerts />
          </OwnerGuard>
        </SignedIn>
      )}
      <div className={`${isFullBleed ? '' : 'max-w-screen-md mx-auto'} ${addTopPadding ? 'pt-20 md:pt-24' : ''} ${addBottomPadding ? 'pb-[calc(6rem+env(safe-area-inset-bottom))]' : ''} ${isFullBleed ? '' : 'p-4 md:p-8'} min-h-screen`}>
        <Routes>
          {/* Test routes - bypass auth, no sign-in required */}
          <Route path="/__test__/" element={<Landing />} />
          <Route path="/__test__/auth/callback" element={<AuthCallback />} />
          <Route path="/__test__/m/:slug" element={<Menu />} />
          <Route path="/__test__/owner/onboarding" element={<Onboarding />} />
          <Route path="/__test__/owner/dashboard" element={<Dashboard />} />
          <Route path="/__test__/owner/settings" element={<BusinessSettings />} />
          <Route path="/__test__/owner/sales" element={<Sales />} />
          <Route path="/__test__/owner/theme" element={<MenuTheme />} />
          <Route path="/__test__/owner/scan" element={<ScanOrder />} />
          <Route path="/__test__/owner/orders" element={<OrderQueue />} />
          <Route path="/__test__/owner/menu" element={<MenuManagement />} />
          <Route path="/__test__/owner/rewards" element={<RewardsConfig />} />
          <Route path="/__test__/sign-in" element={<SignInRedirect />} />
          <Route path="/__test__/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/__test__/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/__test__/order/:orderId/success" element={<OrderSuccess />} />
          <Route path="/__test__/order/:orderId/status" element={<OrderStatus />} />
          <Route path="/__test__/customer" element={<CustomerWelcome />} />
          <Route path="/__test__/profile" element={<Profile />} />
          <Route path="/__test__/account" element={<Account />} />
          <Route path="/__test__/admin" element={<Admin />} />
          <Route path="/" element={<Landing />} />
          <Route path="/customer" element={<CustomerWelcome />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/m/:slug" element={<Menu />} />
          <Route
            path="/owner/onboarding"
            element={
              <>
                <SignedIn><OwnerGuard><Onboarding /></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/dashboard"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><Dashboard /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/settings"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><BusinessSettings /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/sales"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><Sales /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/theme"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><MenuTheme /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/scan"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><ScanOrder /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/orders"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><OrderQueue /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/menu"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><MenuManagement /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/owner/rewards"
            element={
              <>
                <SignedIn><OwnerGuard><OwnerStripeGate><RewardsConfig /></OwnerStripeGate></OwnerGuard></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route path="/sign-in" element={<SignInRedirect />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/order/:orderId/success" element={<OrderSuccess />} />
          <Route path="/order/:orderId/status" element={<OrderStatus />} />
          <Route
            path="/profile"
            element={
              <>
                <SignedIn><Profile /></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/account"
            element={
              <>
                <SignedIn><Account /></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route
            path="/admin"
            element={
              <>
                <SignedIn><Admin /></SignedIn>
                <SignedOut><Navigate to="/" replace /></SignedOut>
              </>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </CartProvider>
  );
}

export default App;
